import { z } from 'zod';

import dayjs from '@/utils/dayjs';
import { unknownToFormString } from '@/utils/unknownToFormString';

import type { ContractValidationScope } from '@/utils/validation/contractValidation';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { TFunction } from 'i18next';

/** True when value is nullish or only whitespace (text/textarea empty check). */
export const isBlankFormString = (value: unknown): boolean =>
  unknownToFormString(value).trim() === '';

const trimFormStringValue = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }
  const trimmed = unknownToFormString(value).trim();
  return trimmed === '' ? null : trimmed;
};

const stringOrNullUnion = z
  .union([z.string(), z.number(), z.null()])
  .optional();

/** API can return null for unpopulated text fields; trims and maps blank to null. */
export const nullableOptionalString = stringOrNullUnion.transform((value) => {
  if (value === undefined) {
    return undefined;
  }
  return trimFormStringValue(value);
});

/** HTML number inputs and API payloads may provide string or number values. */
export const nullableOptionalStringOrNumber = z
  .union([z.string(), z.number(), z.null()])
  .optional();

export type FieldRequiredPredicate =
  | boolean
  | ((values: Record<string, unknown>) => boolean)
  | ((values: Record<string, unknown>, scope: ValidationScope) => boolean)
  | ((
      values: Record<string, unknown>,
      scope: ValidationScope,
      contractScope?: ContractValidationScope,
    ) => boolean);

export type ValidatedField = {
  schema: z.ZodType;
  isRequired: FieldRequiredPredicate;
};

export const scopeRequired = (
  when: ValidationScope | ValidationScope[] = 'complete',
): FieldRequiredPredicate => {
  const scopes = Array.isArray(when) ? when : [when];
  return (_values: Record<string, unknown>, currentScope: ValidationScope) =>
    scopes.includes(currentScope);
};

export function evaluateFieldRequired(
  isRequired: FieldRequiredPredicate,
  values: Record<string, unknown>,
  scope: ValidationScope,
  contractScope?: ContractValidationScope,
): boolean {
  if (typeof isRequired === 'function') {
    if (isRequired.length >= 3) {
      return isRequired(values, scope, contractScope);
    }
    if (isRequired.length >= 2) {
      return isRequired(values, scope);
    }
    return (isRequired as (v: Record<string, unknown>) => boolean)(values);
  }
  return isRequired;
}

export const buildCommonValidationMessages = (t: TFunction) => ({
  required: t('REQUIRED_FIELD'),
  invalidEmail: t('VALIDATION_INVALID_EMAIL'),
  invalidPhone: t('VALIDATION_INVALID_PHONE'),
  maxLength: (number: number) => t('MAX_LENGTH_RULE', { number }),
  notValid: t('NOT_VALID_RULE'),
  pastDate: t('PAST_DATE_RULE'),
  password: t('PASSWORD_RULE'),
  passwordShort: t('PASSWORD_SHORT_RULE'),
  uppercase: t('UPPERCASE_RULE'),
  lowercase: t('LOWERCASE_RULE'),
  numberContain: t('NUMBER_CONTAIN_RULE'),
  symbol: t('SYMBOL_RULE'),
  passwordMismatch: t('PASSWORDS_DO_NOT_MATCH'),
});

const numberOrNullUnion = z
  .union([z.string(), z.number(), z.null()])
  .optional();

export const optionalString = (): ValidatedField => ({
  schema: nullableOptionalString,
  isRequired: false,
});

export const requiredBoolean = (message: string): ValidatedField => ({
  schema: z.unknown().superRefine((value, ctx) => {
    if (value !== true) {
      ctx.addIssue({ code: 'custom', message });
    }
  }),
  isRequired: true,
});

export const requiredString = (message: string): ValidatedField => ({
  schema: stringOrNullUnion
    .superRefine((value, ctx) => {
      if (isBlankFormString(value)) {
        ctx.addIssue({ code: 'custom', message });
      }
    })
    .transform((value) => {
      if (value == null || value === undefined) {
        return value;
      }
      return String(value).trim();
    }),
  isRequired: true,
});

/** Required non-empty value for pickers, uploads, and other non-string controls. */
export const requiredPresentField = (message: string): ValidatedField => ({
  schema: z.unknown().superRefine((value, ctx) => {
    if (value == null || value === '') {
      ctx.addIssue({ code: 'custom', message });
    }
  }),
  isRequired: true,
});

export const withMaxLength = (
  field: ValidatedField,
  maxLength: number,
  message: string,
): ValidatedField => ({
  schema: field.schema.superRefine((value, ctx) => {
    if (value == null || value === '') {
      return;
    }
    const text =
      typeof value === 'string'
        ? value.trim()
        : typeof value === 'number' || typeof value === 'boolean'
          ? String(value).trim()
          : null;
    if (text == null) {
      return;
    }
    if (text.length > maxLength) {
      ctx.addIssue({ code: 'custom', message });
    }
  }),
  isRequired: field.isRequired,
});

const emailSchema = z.email();

/** The one email format rule for forms, invites and screening answers. */
export const isValidEmail = (value: string): boolean =>
  emailSchema.safeParse(value).success;

export const emailField = (messages: {
  required: string;
  invalidEmail: string;
}): ValidatedField => ({
  schema: z
    .union([z.string(), z.null()])
    .optional()
    .superRefine((value, ctx) => {
      if (isBlankFormString(value)) {
        ctx.addIssue({ code: 'custom', message: messages.required });
        return;
      }
      if (!isValidEmail(String(value).trim())) {
        ctx.addIssue({ code: 'custom', message: messages.invalidEmail });
      }
    })
    .transform((value) => {
      if (value == null || value === undefined) {
        return value;
      }
      return String(value).trim();
    }),
  isRequired: true,
});

const PHONE_DIGITS_PATTERN = /^\+?[0-9]{6,15}$/;

/** Strip spaces and common separators so +31 6 12345678 matches the stored format. */
export const normalizePhoneInput = (value: unknown): string => {
  if (value == null) {
    return '';
  }
  return unknownToFormString(value)
    .replace(/[\s\-().]+/g, '')
    .trim();
};

export const phoneField = (
  messages: { required?: string; invalidPhone: string },
  required = false,
): ValidatedField => ({
  schema: z
    .union([z.string(), z.number(), z.null()])
    .optional()
    .superRefine((value, ctx) => {
      const normalized = normalizePhoneInput(value);
      if (!normalized) {
        if (required && messages.required) {
          ctx.addIssue({ code: 'custom', message: messages.required });
        }
        return;
      }
      if (!PHONE_DIGITS_PATTERN.test(normalized)) {
        ctx.addIssue({ code: 'custom', message: messages.invalidPhone });
      }
    }),
  isRequired: required,
});

type PasswordMessages = {
  required: string;
  passwordShort: string;
  uppercase: string;
  lowercase: string;
  numberContain: string;
  symbol: string;
};

export const passwordField = (messages: PasswordMessages): ValidatedField => ({
  schema: z
    .union([z.string(), z.null()])
    .optional()
    .superRefine((value, ctx) => {
      if (isBlankFormString(value)) {
        ctx.addIssue({ code: 'custom', message: messages.required });
        return;
      }
      const stringValue = unknownToFormString(value);
      const uppercaseReg = /(?=.*[A-Z])/;
      const lowercaseReg = /^(?=.*[a-z])/;
      const numberReg = /^(?=.*[0-9])/;
      const symbolReg = /[^A-Za-z0-9]/;
      const longReg = /^(?=.{8,})/;
      if (!longReg.test(stringValue)) {
        ctx.addIssue({ code: 'custom', message: messages.passwordShort });
      } else if (!uppercaseReg.test(stringValue)) {
        ctx.addIssue({ code: 'custom', message: messages.uppercase });
      } else if (!lowercaseReg.test(stringValue)) {
        ctx.addIssue({ code: 'custom', message: messages.lowercase });
      } else if (!numberReg.test(stringValue)) {
        ctx.addIssue({ code: 'custom', message: messages.numberContain });
      } else if (!symbolReg.test(stringValue)) {
        ctx.addIssue({ code: 'custom', message: messages.symbol });
      }
    }),
  isRequired: true,
});

/** Same strength rules as {@link passwordField}; pair with {@link refinePasswordsMatch}. */
export const passwordConfirmField = (
  messages: PasswordMessages,
): ValidatedField => passwordField(messages);

/** Issue on confirm path when password and confirm differ (skips blank values). */
export const assertPasswordsMatch = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
  passwordKey: string,
  confirmKey: string,
  mismatchMessage: string,
): void => {
  const password = unknownToFormString(data[passwordKey]);
  const confirm = unknownToFormString(data[confirmKey]);
  if (isBlankFormString(password) || isBlankFormString(confirm)) {
    return;
  }
  if (password !== confirm) {
    ctx.addIssue({
      code: 'custom',
      path: [confirmKey],
      message: mismatchMessage,
    });
  }
};

/**
 * Ensures confirm equals password. Issues on the confirm path when they differ.
 * Compatible with {@link buildFormSchema} refinements.
 */
export const refinePasswordsMatch = <T extends z.ZodObject<z.ZodRawShape>>(
  schema: T,
  passwordKey: string,
  confirmKey: string,
  mismatchMessage: string,
): z.ZodType =>
  schema.superRefine((data: Record<string, unknown>, ctx) => {
    assertPasswordsMatch(data, ctx, passwordKey, confirmKey, mismatchMessage);
  });

export const isUnsetNumberValue = (value: unknown): boolean =>
  value == null || value === '';

export const isZeroNumberValue = (value: unknown): boolean =>
  value === 0 || value === '0';

const isValidNumberFormat = (value: unknown): boolean => {
  const reg = /\+?\d/g;
  const stringValue = unknownToFormString(value);
  if (!reg.test(stringValue)) {
    return false;
  }
  return Number(value) >= 0;
};

export const optionalNumberField = (messages: {
  notValid: string;
}): ValidatedField => ({
  schema: numberOrNullUnion.superRefine((value, ctx) => {
    if (isUnsetNumberValue(value)) {
      return;
    }
    if (!isValidNumberFormat(value)) {
      ctx.addIssue({ code: 'custom', message: messages.notValid });
    }
  }),
  isRequired: false,
});

export const requiredNonZeroNumberField = (messages: {
  required: string;
  notValid: string;
}): ValidatedField => ({
  schema: numberOrNullUnion.superRefine((value, ctx) => {
    if (isUnsetNumberValue(value)) {
      ctx.addIssue({ code: 'custom', message: messages.required });
      return;
    }
    if (isZeroNumberValue(value)) {
      ctx.addIssue({ code: 'custom', message: messages.notValid });
      return;
    }
    if (!isValidNumberFormat(value)) {
      ctx.addIssue({ code: 'custom', message: messages.notValid });
    }
  }),
  isRequired: true,
});

export const futureDateField = (
  message: string,
  required = true,
): ValidatedField => ({
  schema: z.unknown().superRefine((value, ctx) => {
    if (value == null || value === '') {
      if (required) {
        ctx.addIssue({ code: 'custom', message });
      }
      return;
    }
    const parsed = dayjs(value as string | number | Date);
    if (!parsed.isValid()) {
      ctx.addIssue({ code: 'custom', message });
      return;
    }
    if (parsed.isBefore(dayjs(), 'day')) {
      ctx.addIssue({ code: 'custom', message });
    }
  }),
  isRequired: required,
});

export const requiredArrayField = (message: string): ValidatedField => ({
  schema: z
    .array(z.unknown())
    .optional()
    .superRefine((value, ctx) => {
      if (!value || value.length === 0) {
        ctx.addIssue({ code: 'custom', message });
      }
    }),
  isRequired: true,
});

export const optionalArrayField = (): ValidatedField => ({
  schema: z.array(z.unknown()).optional(),
  isRequired: false,
});

type ApplyProgressFieldRefinesOptions = {
  rejectZeroForFields?: readonly string[];
};

export const applyProgressFieldRefines = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
  fieldNames: string[],
  requiredMessage: string,
  options?: ApplyProgressFieldRefinesOptions,
) => {
  const rejectZero = new Set(options?.rejectZeroForFields ?? []);
  fieldNames.forEach((fieldName) => {
    const value = data[fieldName];
    const isEmpty =
      value == null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'string' || typeof value === 'number'
        ? isBlankFormString(value)
        : false);
    const isRejectedZero =
      rejectZero.has(fieldName) && isZeroNumberValue(value);
    if (isEmpty || isRejectedZero) {
      ctx.addIssue({
        code: 'custom',
        path: [fieldName],
        message: requiredMessage,
      });
    }
  });
};

const officeLocationEntrySchema = z.object({
  id: z.string(),
  address: z.string(),
});

export const optionalOfficeLocationsField = (): ValidatedField => ({
  schema: z.array(officeLocationEntrySchema).optional(),
  isRequired: false,
});

export const validateOfficeLocationsRefine = (
  data: Record<string, unknown>,
  ctx: z.RefinementCtx,
  requiredMessage: string,
  path = 'office_locations',
) => {
  const locations = data[path] as { address?: string }[] | undefined;
  if (!locations?.length) {
    return;
  }
  locations.forEach((location, index) => {
    if (!location?.address?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: [path, index, 'address'],
        message: requiredMessage,
      });
    }
  });
};
